import { Component, DetailEvent, Entity, registerComponent } from "aframe";
import { AnimationActionInstance } from "./animation/animation-action";
import { AvatarActionFactory, AvatarSystem } from "./avatar";
import { InteractionEventDetail } from "./interaction/event-emitter-action";
import { AvatarInteractionSystem } from "./interaction/interaction";
import { SlotComponent, SlotEntity, SlotEvent } from "./slot";


// TODO : find a better name (like something-proxy )

export interface InventoryItemData
{
	item? : Entity;
	action? : string;
	label? : string;
	icon? : string;
	takeAnimation : string;
	putAnimation : string;
	accept? : string[];
	interactionEvents : string[];
}
export interface InventoryItemComponent extends Component
{
	interactionListener : EventListener;
	data : InventoryItemData;
	isEntityConnection : boolean;
	getConnectedEntities() : Entity[];
	activate(slotEl? : SlotEntity) : void;
}

registerComponent("inventory-item", {
	schema : { 
		item : { type : "selector" },
		action : { type : "string" },
		label : { type : "string" },
		icon : { type : "string" },
		takeAnimation : { type : "string", default : "Fetch" },
		putAnimation : { type : "string", default : "Store" },
		accept : { type : "array", default : [] }, // list of selectors ; items can be put if they match at least one.
		interactionEvents : { type : "array", default : ["attach-to-slot", "activate", "fire"] }
	},
	init(this : InventoryItemComponent)
	{
		this.isEntityConnection = true;

		this.interactionListener = (event : Event) =>
		{
			if(event.type == 'attach-to-slot') //Grab event
			{
				this.activate((event as DetailEvent<SlotEntity>).detail );
			}
			else
			{
				const target = (event as DetailEvent<InteractionEventDetail>).detail?.interactor;
				if(target)
					this.activate(target);
				else
					console.warn("Cannot handle event", event.type);
			}
			
			event.preventDefault();
			event.stopPropagation();
		};

		const disconnectedListener  = (event : DetailEvent<{ el : Entity }>) =>
		{
			const el = event.detail.el;
			if( (el === this.data.item) && !el.isConnected ) //Removed from DOM.
				this.el.setAttribute(this.attrName!, { item : null });
		};
		this.el.sceneEl!.addEventListener("child-detached", disconnectedListener as EventListener);
	},
	activate(this : InventoryItemComponent, slot? : SlotEntity)
	{
		const item = this.data.item;
		if(!item) return;

		const attachedListener = ((event : SlotEvent) => {
			//TODO
		}) as EventListener;

		item.addEventListener("attached-to-slot", attachedListener);
		item.emit("attach-to-slot", slot);
		item.removeEventListener("attached-to-slot", attachedListener);
	},
	getConnectedEntities(this : InventoryItemComponent)
	{
		return this.data.item ? [ this.data.item ] : [];
	},
	update(this : InventoryItemComponent, oldData : InventoryItemData)
	{
		const item = this.data.item, oldItem = oldData && oldData.item;
		if(item !== oldItem)
		{
			//Unidirectional connection.
			if(oldItem)
			{
				//oldItem.emit("disconnected-from-entity", { entity : this.el, connection : this } );
				this.el.emit("disconnected-from-entity", { entity : oldItem, connection : this } );
			}
			if(item)
			{
				//this.data.item.emit("connected-to-entity", { entity : this.el, connection : this } );
				this.el.emit("connected-to-entity", { entity : item, connection : this } );
			}
		}

		for(let oldEvent of (oldData && oldData.interactionEvents) || [] )
			this.el.removeEventListener(oldEvent, this.interactionListener);

		for(let event of (this.data.interactionEvents || []))
			this.el.addEventListener(event, this.interactionListener);

		const sceneEl = this.el.sceneEl!;
		const avatarSystem = sceneEl.systems["avatar"] as AvatarSystem;
		if(oldData && oldData.action)
			delete avatarSystem.actions[ oldData.action ];

		if(this.data.action)
		{
			let factory : AvatarActionFactory | null = null;
			const accept = this.data.accept || [];
			if(item || (this.data.accept || []).length)
			{
				factory = (avatar, input, source) =>
				{
					if(accept.length)
					{
						const sourceSlot = source && source.components && source.components.slot as SlotComponent;
						const sourceItem = (sourceSlot && sourceSlot.data && sourceSlot.data.item);
						if(sourceItem)
							for(let condition of accept)
							{
								if(sourceItem.matches(condition))
								{
									this.el.setAttribute(this.attrName!, { item : sourceItem } );
									return;
								}
							}
					}
					
					
					if( !item ) return; //Just accept.

					if( item.parentEl && (item.parentEl !== item.parentNode) && item.parentEl.components.slot )
					{
						// item is inside another slot : put it back
						const instance  = new AnimationActionInstance(avatar, input, this.data.putAnimation, item.parentEl);
						instance.finished.then( () => item.emit("detach-from-slot", item.parentEl) );
						return instance;
					}

					if(!(source && source.components && source.components.slot)) //Action was not emitted through a Slot (aka hand)
					{
						source = undefined;
						const interactionSystem = sceneEl.systems["avatar-interaction"] as AvatarInteractionSystem;
						for(let slot of [ interactionSystem.rightHand.slot, interactionSystem.leftHand.slot ])
						{
							const slotData = slot && slot.components && slot.components.slot && slot.components.slot.data;
							if(slotData && !slotData.item)
							{
								source = slot!;
								break;
							}
						}
					}

					// item is in its natural parent (inventory) : take it
					const instance  = new AnimationActionInstance(avatar, input, this.data.takeAnimation, source);
					instance.finished.then( () => this.activate( (source || input.input!.el) as SlotEntity) );
					return instance;
				};
				factory.label = this.data.label;
				factory.icon = this.data.icon;
				avatarSystem.actions[this.data.action] = factory;
			}
			else
			{
				delete avatarSystem.actions[ this.data.action ];
			}	
			
			// actions or interactions ???
			this.el.emit("actions-changed", { [ this.data.action ] : factory } );
		}
	}
});

